import { memo, useEffect, useRef, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BAADocumentBody } from "@/lib/legal/baa-content";

interface Props {
  onScrolledToEnd: () => void;
  className?: string;
}

// Memoize the document body so scroll-driven parent re-renders don't
// re-render the (large) BAA markup. Content is static.
const MemoBAABody = memo(BAADocumentBody);

/**
 * Scrollable panel that renders the full BAA inline. Calls onScrolledToEnd
 * the first time the user scrolls within ~16px of the bottom, which the
 * parent uses to enable the "Sign" button (anti-blind-click gate).
 */
export function BAAReviewPanel({ onScrolledToEnd, className }: Props) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const firedRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const onScrolledToEndRef = useRef(onScrolledToEnd);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [progress, setProgress] = useState(0);

  // Keep latest callback without retriggering the scroll-listener effect.
  useEffect(() => {
    onScrolledToEndRef.current = onScrolledToEnd;
  }, [onScrolledToEnd]);

  useEffect(() => {
    // The Radix ScrollArea viewport is the actual scrolling element.
    const viewport = viewportRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement | null;
    if (!viewport) return;

    function evaluate() {
      rafRef.current = null;
      const { scrollTop, scrollHeight, clientHeight } = viewport!;
      const scrollable = scrollHeight - clientHeight;
      // Update progress (0–100). If content fits, we're effectively at 100%.
      const pct =
        scrollable < 8
          ? 100
          : Math.min(100, Math.max(0, (scrollTop / scrollable) * 100));
      setProgress(pct);

      if (firedRef.current) return;
      // Already short enough that everything fits — auto-pass.
      if (scrollable < 8) {
        firedRef.current = true;
        setReachedEnd(true);
        onScrolledToEndRef.current();
        return;
      }
      if (scrollTop + clientHeight >= scrollHeight - 16) {
        firedRef.current = true;
        setReachedEnd(true);
        onScrolledToEndRef.current();
      }
    }

    // rAF-throttled scroll handler — at most one check per frame, keeping
    // the scrolling thread free for compositing on mobile.
    function onScroll() {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(evaluate);
    }

    viewport.addEventListener("scroll", onScroll, { passive: true });
    // Run once after layout in case content already fits.
    rafRef.current = requestAnimationFrame(evaluate);

    return () => {
      viewport.removeEventListener("scroll", onScroll);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return (
    <div className={className}>
      <div
        ref={viewportRef}
        className="rounded-xl border bg-card"
        // Promote to its own layer so scrolling doesn't repaint surrounding UI.
        style={{ contain: "content", willChange: "transform" }}
      >
        <ScrollArea className="h-[360px] p-6">
          <MemoBAABody />
          <div className="mt-6 border-t pt-4 text-xs text-muted-foreground">
            — End of document —
          </div>
        </ScrollArea>
      </div>
      <div className="mt-2 space-y-1">
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-label="Document review progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-150 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {reachedEnd
              ? "✓ You've reviewed the full agreement."
              : "Scroll to the end of the document to enable the signature step."}
          </span>
          <span className="tabular-nums">{Math.round(progress)}%</span>
        </div>
      </div>
    </div>
  );
}